import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import {
  CheckCircle, Clock, Truck, Package, MapPin, Search,
  AlertCircle, Loader2,
} from "lucide-react";

const STATUT_LABELS = {
  en_attente: "En attente de livraison",
  en_cours: "En route vers vous...",
  livree: "Livré !",
  reportee: "Livraison reportée",
  annulee: "Livraison annulée",
};

const STEPS = [
  { key: "en_attente", label: "Commande confirmée", icon: Package },
  { key: "en_cours", label: "En cours de livraison", icon: Truck },
  { key: "livree", label: "Livré", icon: CheckCircle },
];

const stepIndex = (statut) => {
  if (statut === "livree") return 2;
  if (statut === "en_cours") return 1;
  return 0;
};

const SuiviCommandePublic = () => {
  const { numero: urlNumero } = useParams();
  const [numero, setNumero] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  // Auto-fill from URL path /c/:numero or query param ?c=
  useEffect(() => {
    const num = urlNumero || new URLSearchParams(window.location.search).get("c");
    if (num) {
      setNumero(num);
      searchCommande(num);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlNumero]);

  const searchCommande = async (num) => {
    const clean = (num || "").replace(/[^0-9]/g, "");
    if (!clean) return;
    setLoading(true);
    setError(null);
    setData(null);
    setConfirmed(null);
    try {
      const res = await axios.get(`${API}/suivi-commande/${clean}`);
      setData(res.data);
      // Only show confirmed if ACTUALLY confirmed in DB
      if (res.data.confirmation_presence && res.data.confirmation_presence !== "en_attente") {
        setConfirmed(res.data.confirmation_presence);
      }
    } catch {
      setError("Commande introuvable. Verifiez votre numero de commande.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await searchCommande(numero);
  };

  const handleConfirmation = async (choice) => {
    if (!data?.suivi_token) return;
    setConfirming(true);
    try {
      await axios.put(`${API}/suivi/${data.suivi_token}/confirmation`, { confirmation: choice });
      setConfirmed(choice);
    } catch {
      // ignore
    } finally {
      setConfirming(false);
    }
  };

  const current = data ? stepIndex(data.statut) : 0;
  const company = data?.company_name || "TopCocoon";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex flex-col items-center px-4 py-8" data-testid="suivi-commande-page">
      {/* Header */}
      <div className="text-center mb-8">
        {data?.logo_url ? (
          <img src={data.logo_url} alt={company} className="h-16 mx-auto mb-3 object-contain" />
        ) : (
          <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Package className="w-8 h-8 text-white" />
          </div>
        )}
        <h1 className="text-2xl font-bold text-slate-800">Suivi Commande {company}</h1>
        <p className="text-slate-500 mt-1 text-sm">Entrez votre numero de commande pour suivre votre livraison</p>
      </div>

      {/* Search form */}
      <form onSubmit={handleSearch} className="w-full max-w-md mb-8" data-testid="search-form">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">#</span>
            <input
              type="text"
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              placeholder="1001"
              className="w-full pl-8 pr-4 py-3 border-2 border-slate-200 rounded-xl text-lg font-medium focus:border-indigo-500 focus:outline-none transition-colors"
              data-testid="numero-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="search-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            Rechercher
          </button>
        </div>
      </form>

      {/* Error */}
      {error && (
        <div className="w-full max-w-md p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 mb-6" data-testid="error-message">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Result */}
      {data && (
        <div className="w-full max-w-md space-y-4" data-testid="commande-result">
          {/* Order number badge */}
          <div className="bg-white rounded-2xl shadow-sm border p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="text-xs text-slate-400 uppercase tracking-wider">Commande</span>
                <p className="text-2xl font-bold text-indigo-600">#{data.numero_commande}</p>
              </div>
              <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                data.statut === "livree" ? "bg-green-100 text-green-700" :
                data.statut === "en_cours" ? "bg-blue-100 text-blue-700" :
                data.statut === "reportee" ? "bg-amber-100 text-amber-700" :
                data.statut === "annulee" ? "bg-red-100 text-red-700" :
                "bg-slate-100 text-slate-600"
              }`} data-testid="statut-badge">
                {STATUT_LABELS[data.statut] || data.statut}
              </span>
            </div>

            {/* Progress steps */}
            <div className="flex items-center justify-between mb-6">
              {STEPS.map((step, idx) => {
                const Icon = step.icon;
                const active = idx <= current;
                const done = idx < current;
                return (
                  <div key={step.key} className="flex items-center flex-1">
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        done ? "bg-green-500 text-white" :
                        active ? "bg-indigo-600 text-white" :
                        "bg-slate-100 text-slate-400"
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className={`text-[10px] mt-1 text-center ${active ? "text-slate-700 font-medium" : "text-slate-400"}`}>
                        {step.label}
                      </span>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-1 mt-[-18px] ${done ? "bg-green-400" : "bg-slate-200"}`} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Details */}
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-slate-700">{data.adresse}</p>
                  <p className="text-slate-500">{data.code_postal} {data.ville}</p>
                </div>
              </div>
              {data.details_commande && (
                <div className="flex items-start gap-3">
                  <Package className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <p className="text-slate-700">{data.details_commande} ({data.quantite} article{data.quantite > 1 ? "s" : ""})</p>
                </div>
              )}
              {data.heure_estimee && (
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-slate-400 shrink-0" />
                  <p className="text-slate-700">Passage prevu vers <strong>{data.heure_estimee}</strong></p>
                </div>
              )}
              {data.date_livraison_prevue && (
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-slate-400 shrink-0" />
                  <p className="text-slate-700">Date prevue : <strong>{new Date(data.date_livraison_prevue).toLocaleDateString("fr-FR")}</strong></p>
                </div>
              )}
            </div>
          </div>

          {/* Confirmation presence */}
          {data.statut !== "livree" && data.statut !== "annulee" && (
            <div className="bg-white rounded-2xl shadow-sm border p-5">
              {confirmed ? (
                <div className={`text-center p-3 rounded-xl ${confirmed === "confirme" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                  <CheckCircle className="w-6 h-6 mx-auto mb-1" />
                  <p className="font-medium text-sm">
                    {confirmed === "confirme" ? "Presence confirmee" : "Absence signalee"}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 text-center font-medium">Serez-vous present(e) pour la livraison ?</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleConfirmation("confirme")}
                      disabled={confirming}
                      className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                      data-testid="confirm-presence-btn"
                    >
                      Oui, je serai la
                    </button>
                    <button
                      onClick={() => handleConfirmation("absent")}
                      disabled={confirming}
                      className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600 disabled:opacity-50"
                      data-testid="confirm-absent-btn"
                    >
                      Non, absent(e)
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <p className="text-xs text-slate-400 mt-12">{company} — Suivi de commande</p>
    </div>
  );
};

export default SuiviCommandePublic;
